import React, { useState } from "react";
import { FaDownload, FaRedo } from "react-icons/fa";

/**
 * Extra job-card actions for the schedulable Shopify tools (price / tag /
 * reconcile): download the ORIGINAL input file of a scheduled or cancelled
 * job, and restart a cancelled job — either immediately or re-scheduled for
 * a new time.
 *
 * Presentational only. The owning tool supplies the API calls via
 * `onDownloadInput(job)` and `onRestart(job, scheduledAtLocal | null)`, where
 * `scheduledAtLocal` is the raw <input type="datetime-local"> value (the tool
 * validates + converts it, mirroring its submit flow). Renders nothing unless
 * the job can download its input or be restarted.
 */
export default function JobInputActions({
  job,
  onDownloadInput,
  downloadingInput = false,
  onRestart,
  restarting = false,
  minScheduledAt,
}) {
  const [showSchedule, setShowSchedule] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");

  const status = job?.status;
  const isScheduled = status === "scheduled";
  const isCancelled = status === "cancelled";
  const canDownloadInput = Boolean(job?.has_input) && (isScheduled || isCancelled);

  if (!canDownloadInput && !isCancelled) return null;

  const closeSchedule = () => {
    setShowSchedule(false);
    setScheduledAt("");
  };

  return (
    <div className="tool-job-extra-actions">
      {canDownloadInput ? (
        <button
          type="button"
          className="tool-btn-secondary"
          onClick={() => onDownloadInput(job)}
          disabled={downloadingInput}
        >
          <FaDownload /> {downloadingInput ? "Downloading…" : "Download Input"}
        </button>
      ) : null}

      {isCancelled ? (
        showSchedule ? (
          <div className="tool-job-restart-panel">
            <label className="tool-label">Restart at</label>
            <input
              type="datetime-local"
              className="tool-input"
              value={scheduledAt}
              min={minScheduledAt}
              onChange={(event) => setScheduledAt(event.target.value)}
              disabled={restarting}
            />
            <div className="tool-job-restart-row">
              <button
                type="button"
                className="tool-btn-primary"
                onClick={() => onRestart(job, scheduledAt || null)}
                disabled={restarting || !scheduledAt}
              >
                {restarting ? "Scheduling…" : "Schedule restart"}
              </button>
              <button
                type="button"
                className="tool-link-button"
                onClick={closeSchedule}
                disabled={restarting}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="tool-job-restart-row">
            <button
              type="button"
              className="tool-btn-secondary"
              onClick={() => onRestart(job, null)}
              disabled={restarting}
            >
              <FaRedo /> {restarting ? "Restarting…" : "Restart now"}
            </button>
            <button
              type="button"
              className="tool-link-button"
              onClick={() => setShowSchedule(true)}
              disabled={restarting}
            >
              Reschedule…
            </button>
          </div>
        )
      ) : null}
    </div>
  );
}
